import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    HiMapPin,
    HiPlusCircle,
    HiChevronRight,
    HiXMark,
    HiArrowLeft,
    HiPhone,
    HiChevronDown
} from 'react-icons/hi2';
import {
    FaCar,
    FaCarSide,
    FaWheelchair,
    FaShareAlt,
    FaCommentDots,
    FaSmile,
    FaPaperPlane,
    FaChevronLeft,
    FaGoogle,
    FaApple
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

import * as api from '../services/api';

// Import assets (used as fallbacks when a ride type has no image_url)
import standardCar from '../assets/standard_car.png';
import suvCar from '../assets/suv_car.png';
import vanCar from '../assets/van_car.png';
import driverProfile from '../assets/driver_profile.png';

// ── helpers ──────────────────────────────────────────────────
const iconForCategory = (name = '') => {
    const n = name.toLowerCase();
    if (/premium|lux|black|exec|business/.test(n)) return FaCar;
    if (/assist|wheel|access|handi|adapt/.test(n)) return FaWheelchair;
    return FaCarSide;
};

const imageForCar = (car = {}) => {
    if (car.image_url && /^https?:\/\//.test(car.image_url)) return car.image_url;
    const s = `${car.body_type || ''} ${car.label || ''} ${car.key || ''}`.toLowerCase();
    if (/van|shuttle|xl|group/.test(s)) return vanCar;
    if (/suv|cross/.test(s)) return suvCar;
    return standardCar;
};

const money = (currency, amount) => {
    if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
    try {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: currency || 'CAD',
            maximumFractionDigits: 2
        }).format(amount);
    } catch {
        return `${currency || 'C$'} ${Number(amount).toFixed(2)}`;
    }
};

const prettyStatus = (s = '') =>
    s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const BookingPage = () => {
    const [sidebarStep, setSidebarStep] = useState('details'); // details, selection, request, searching, arriving, chat
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);
    const [step, setStep] = useState('booking'); // modals: booking, for_whom, phone, otp
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    // Geography + catalogue
    const [markets, setMarkets] = useState([]);
    const [marketId, setMarketId] = useState(null);
    const [currency, setCurrency] = useState('CAD');
    const [categories, setCategories] = useState([]); // [{category, cars:[...]}]
    const [activeCategory, setActiveCategory] = useState(null);

    // Trip
    const [pickupText, setPickupText] = useState('');
    const [dropoffText, setDropoffText] = useState('');
    const [pickup, setPickup] = useState(null); // {lat,lng,address}
    const [dropoff, setDropoff] = useState(null);
    const [notes, setNotes] = useState('');
    const [promo, setPromo] = useState('');
    const [payment, setPayment] = useState('Cash');

    // Estimates keyed by vehicle_class
    const [estimates, setEstimates] = useState({});
    const [selectedCar, setSelectedCar] = useState(null);

    // Auth
    const [pendingPhone, setPendingPhone] = useState('');
    const [pendingName, setPendingName] = useState('');
    const [devHint, setDevHint] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const otpRefs = useRef([]);
    const [authed, setAuthed] = useState(api.isAuthed());

    // Live ride
    const [ride, setRide] = useState(null);
    const pollRef = useRef(null);

    const loadCatalog = useCallback(async (mid) => {
        try {
            const cat = await api.getCarTypes(mid);
            setCurrency(cat.currency || 'CAD');
            setCategories(cat.categories || []);
            setActiveCategory((prev) =>
                (cat.categories || []).some((c) => c.category === prev)
                    ? prev
                    : cat.categories?.[0]?.category || null
            );
        } catch (e) {
            setError(e.message);
        }
    }, []);

    // ── load markets + default catalogue on mount ──
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const ms = await api.getMarkets();
                if (!alive) return;
                setMarkets(ms);
                // Prefer the brand's home markets (Winnipeg / Vancouver),
                // then any live market, then whatever exists.
                const home = ms.find((m) => /winnipeg|vancouver/i.test(m.name));
                const def = home || ms.find((m) => m.is_live) || ms[0];
                if (def) {
                    setMarketId(def.id);
                    await loadCatalog(def.id);
                }
            } catch (e) {
                setError(e.message);
            }
        })();
        return () => {
            alive = false;
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [loadCatalog]);

    // ── estimates for the visible category whenever trip/category changes ──
    useEffect(() => {
        if (!pickup || !dropoff || !marketId || !activeCategory) return;
        const cars =
            categories.find((c) => c.category === activeCategory)?.cars || [];
        if (!cars.length) return;
        let alive = true;
        (async () => {
            const results = await Promise.all(
                cars.map(async (car) => {
                    try {
                        const q = await api.estimateFare({
                            marketId,
                            vehicleClass: car.vehicle_class,
                            pickup,
                            dropoff,
                            promoCode: promo
                        });
                        return [car.vehicle_class, q];
                    } catch {
                        return [car.vehicle_class, null];
                    }
                })
            );
            if (!alive) return;
            setEstimates((prev) => {
                const next = { ...prev };
                results.forEach(([k, v]) => {
                    if (v) next[k] = v;
                });
                return next;
            });
        })();
        return () => {
            alive = false;
        };
    }, [pickup, dropoff, marketId, activeCategory, categories, promo]);

    const priceFor = (car) => {
        const est = estimates[car.vehicle_class];
        if (est) return money(est.currency || currency, est.total);
        return money(currency, car.starting_price) + '+';
    };
    const etaFor = (car) => (car.eta_min ? `${car.eta_min} min` : '—');

    const activeCars =
        categories.find((c) => c.category === activeCategory)?.cars || [];

    // ── flow: details → selection (geocode + market detect) ──
    const proceedToSelection = async (category) => {
        setError('');
        if (!pickupText.trim() || !dropoffText.trim()) {
            setError('Enter both a pickup and a destination.');
            return;
        }
        setBusy(true);
        try {
            const [p, d] = await Promise.all([
                api.geocode(pickupText),
                api.geocode(dropoffText)
            ]);
            if (!p) throw new Error(`Couldn't locate "${pickupText}".`);
            if (!d) throw new Error(`Couldn't locate "${dropoffText}".`);
            setPickup(p);
            setDropoff(d);

            const near = api.nearestMarket(markets, p.lat, p.lng);
            if (near && near.id !== marketId) {
                setMarketId(near.id);
                await loadCatalog(near.id);
            }
            if (category) setActiveCategory(category);
            setSelectedCar(null);
            setSidebarStep('selection');
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    // ── auth: send OTP ──
    const sendOtp = async () => {
        setError('');
        if (!pendingPhone.trim()) {
            setError('Enter your phone number.');
            return;
        }
        setBusy(true);
        try {
            const res = await api.requestOtp(pendingPhone.trim());
            setDevHint(res.dev_hint || '');
            setOtp(['', '', '', '', '', '']);
            setStep('otp');
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    // ── auth: verify OTP → then place the ride ──
    const verifyAndBook = async () => {
        setError('');
        const code = otp.join('');
        if (code.length < 6) {
            setError('Enter the 6-digit code.');
            return;
        }
        setBusy(true);
        try {
            await api.verifyOtp(pendingPhone.trim(), code, pendingName.trim());
            setAuthed(true);
            setStep('booking');
            await placeRide();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    // ── create the ride ──
    const placeRide = async () => {
        if (!api.isAuthed()) {
            setStep('phone');
            return;
        }
        if (!selectedCar || !pickup || !dropoff || !marketId) {
            setError('Pick a car and set your route first.');
            return;
        }
        setError('');
        setBusy(true);
        try {
            const r = await api.createRide({
                marketId,
                vehicleClass: selectedCar.vehicle_class,
                pickup,
                dropoff,
                promoCode: promo
            });
            setRide(r);
            setSidebarStep('searching');
            startPolling(r.id);
        } catch (e) {
            setError(e.message);
            setSidebarStep('request');
        } finally {
            setBusy(false);
        }
    };

    const startPolling = (rideId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks += 1;
            try {
                const r = await api.getRide(rideId);
                setRide(r);
                const assigned =
                    r.driver_id || /assign|accept|arriv|progress|en_route/i.test(r.status);
                const done = /complete|cancel|no_driver|expired/i.test(r.status);
                if (assigned || done || ticks >= 4) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    setSidebarStep('arriving');
                }
            } catch {
                // keep the searching screen; user can cancel
            }
        }, 2500);
    };

    const doCancelRide = async () => {
        if (pollRef.current) clearInterval(pollRef.current);
        try {
            if (ride?.id) await api.cancelRide(ride.id);
        } catch {
            /* ignore — reset UI regardless */
        }
        setRide(null);
        setSidebarStep('details');
    };

    const setOtpDigit = (i, v) => {
        const digit = v.replace(/\D/g, '').slice(-1);
        setOtp((prev) => {
            const next = [...prev];
            next[i] = digit;
            return next;
        });
        if (digit && i < 5) otpRefs.current[i + 1]?.focus();
    };

    // Map centre follows pickup → market → default
    const activeMarket = markets.find((m) => m.id === marketId);
    const mapCenter = pickup
        ? `${pickup.lat},${pickup.lng}`
        : activeMarket
        ? `${activeMarket.center_lat},${activeMarket.center_lng}`
        : 'Winnipeg';
    const marketName = activeMarket?.name;

    // ── error banner ──
    const ErrorBanner = () =>
        error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 text-xs font-medium rounded-xl px-4 py-3 flex items-start justify-between gap-3">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
                    <HiXMark />
                </button>
            </div>
        ) : null;

    // ─────────────────────────── steps ───────────────────────────
    const renderRideDetails = () => (
        <div className="flex flex-col gap-6">
            <div className="text-center mt-4">
                <h2 className="text-xl audiowide-regular uppercase text-[#0E0E0E]">
                    Enter Your Ride Details
                </h2>
                {marketName && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1660C3] bg-[#1660C3]/10 px-3 py-1 rounded-full mt-2">
                        <HiMapPin /> {marketName}
                    </span>
                )}
            </div>

            <ErrorBanner />

            <div className="space-y-4">
                <div className="flex items-center gap-4 border-b border-zinc-200 pb-2">
                    <div className="w-6 h-6 rounded-full border-4 border-black flex-shrink-0" />
                    <input
                        type="text"
                        placeholder="From"
                        value={pickupText}
                        onChange={(e) => setPickupText(e.target.value)}
                        className="w-full bg-transparent outline-none text-[#0E0E0E] font-medium dm-sans text-sm"
                    />
                </div>

                <div className="flex items-center gap-4 border-b border-zinc-200 pb-2">
                    <div className="w-6 h-6 rounded-full border-4 border-[#1660C3] flex-shrink-0" />
                    <input
                        type="text"
                        placeholder="To"
                        value={dropoffText}
                        onChange={(e) => setDropoffText(e.target.value)}
                        className="w-full bg-transparent outline-none text-[#0E0E0E] font-medium dm-sans text-sm"
                    />
                </div>

                <button className="flex items-center gap-2 text-[#1660C3] text-sm font-bold dm-sans pt-2">
                    <HiPlusCircle className="text-xl" /> Add Stops
                </button>
            </div>

            <div className="border-t border-zinc-100 pt-6">
                <h3 className="text-[12px] audiowide-regular uppercase text-center mb-6 text-zinc-900">
                    Choose Service Class
                </h3>
                <div className="grid grid-cols-3 gap-4">
                    {(categories.length
                        ? categories.slice(0, 3)
                        : [{ category: 'Standard' }, { category: 'Premium' }, { category: 'Assist' }]
                    ).map((c) => {
                        const Icon = iconForCategory(c.category);
                        const cheapest =
                            c.cars &&
                            c.cars.reduce(
                                (min, car) =>
                                    Math.min(min, Number(car.starting_price) || Infinity),
                                Infinity
                            );
                        const active = activeCategory === c.category;
                        return (
                            <button
                                key={c.category}
                                onClick={() => proceedToSelection(c.category)}
                                disabled={busy}
                                className="flex flex-col items-center group transition-all duration-300 disabled:opacity-50"
                            >
                                <div
                                    className={`w-16 h-16 rounded-xl flex items-center justify-center mb-2 shadow-sm transition-all ${
                                        active
                                            ? 'bg-[#1660C3] text-white'
                                            : 'bg-zinc-100 text-[#1660C3] group-hover:bg-zinc-200'
                                    }`}
                                >
                                    <Icon className="text-2xl" />
                                </div>
                                <span className="text-xs font-bold text-zinc-900 mb-0.5">
                                    {c.category}
                                </span>
                                <span className="text-[10px] text-zinc-400 font-medium">
                                    {cheapest && cheapest !== Infinity
                                        ? `From ${money(currency, cheapest)}`
                                        : ' '}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mt-4 space-y-4">
                <textarea
                    placeholder="Driver Instruction"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full bg-zinc-100/50 border border-transparent rounded-xl p-4 outline-none text-sm dm-sans h-24 resize-none focus:bg-white focus:border-zinc-200 transition-all"
                />

                <button
                    onClick={() => setStep('for_whom')}
                    className="w-full flex items-center justify-between bg-zinc-100/50 p-4 rounded-xl hover:bg-zinc-100 transition-colors"
                >
                    <span className="text-sm font-bold text-zinc-600">For Someone Else</span>
                    <HiChevronRight className="text-zinc-600" />
                </button>

                <button
                    onClick={() => proceedToSelection(activeCategory)}
                    disabled={busy}
                    className="w-full bg-gradient-to-r from-[#1660C3] to-[#2671D8] text-white py-4 rounded-xl font-bold dm-sans uppercase tracking-[1px] shadow-lg shadow-blue-200/50 hover:opacity-90 transition-opacity mt-2 disabled:opacity-60"
                >
                    {busy ? 'Locating…' : 'Continue'}
                </button>
            </div>
        </div>
    );

    const renderCarSelection = () => (
        <div className="flex flex-col h-full bg-[#F9F9F9] -mx-6 md:-mx-8 -my-8 p-6 md:p-8 overflow-y-auto scrollbar-hide">
            <h2 className="text-xl audiowide-regular uppercase text-[#0E0E0E] text-center mt-4 mb-6 md:mb-8">
                Choose Your Car
            </h2>

            <div className="space-y-4 bg-white p-4 rounded-2xl mb-6 shadow-sm">
                <div className="flex items-center gap-4 border-b border-zinc-100 pb-2">
                    <div className="w-6 h-6 rounded-full border-4 border-black flex-shrink-0" />
                    <input
                        type="text"
                        value={pickup?.address || pickupText}
                        readOnly
                        className="w-full bg-transparent outline-none text-[#0E0E0E] font-medium dm-sans text-xs truncate"
                    />
                </div>
                <div className="flex items-center gap-4 border-b border-zinc-100 pb-2">
                    <div className="w-6 h-6 rounded-full border-4 border-[#1660C3] flex-shrink-0" />
                    <input
                        type="text"
                        value={dropoff?.address || dropoffText}
                        readOnly
                        className="w-full bg-transparent outline-none text-[#0E0E0E] font-medium dm-sans text-xs truncate"
                    />
                </div>
                <button
                    onClick={() => setSidebarStep('details')}
                    className="flex items-center gap-2 text-[#1660C3] text-xs font-bold dm-sans"
                >
                    <HiArrowLeft className="text-sm" /> Edit route
                </button>
            </div>

            <ErrorBanner />

            <div className="border-t border-zinc-200 pt-6">
                <div className="flex gap-3 overflow-x-auto scrollbar-hide mb-6 pb-1">
                    {categories.map((c) => {
                        const Icon = iconForCategory(c.category);
                        const active = activeCategory === c.category;
                        return (
                            <button
                                key={c.category}
                                onClick={() => {
                                    setActiveCategory(c.category);
                                    setSelectedCar(null);
                                }}
                                className="flex flex-col items-center group flex-shrink-0"
                            >
                                <div
                                    className={`w-14 h-14 rounded-xl flex items-center justify-center mb-2 shadow-sm transition-all ${
                                        active
                                            ? 'bg-[#1660C3] text-white'
                                            : 'bg-white text-[#1660C3] group-hover:bg-zinc-50'
                                    }`}
                                >
                                    <Icon className="text-xl" />
                                </div>
                                <span className="text-[11px] font-bold text-zinc-900 whitespace-nowrap">
                                    {c.category}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-bold text-zinc-800">{activeCategory} Cars</h4>
                    <span className="text-xs text-zinc-400 font-medium">
                        {activeCars.length} Options
                    </span>
                </div>

                <div className="space-y-3 mb-8">
                    {activeCars.length === 0 && (
                        <p className="text-xs text-zinc-400 text-center py-8">
                            No cars available here yet.
                        </p>
                    )}
                    {activeCars.map((car) => (
                        <div
                            key={car.key}
                            onClick={() => setSelectedCar(car)}
                            className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all border-2 ${
                                selectedCar?.key === car.key
                                    ? 'bg-[#60A3FF] border-transparent'
                                    : 'bg-white border-transparent hover:border-zinc-100'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <img
                                    src={imageForCar(car)}
                                    alt={car.label}
                                    className="w-24 h-auto object-contain"
                                />
                                <div>
                                    <h5
                                        className={`font-bold text-sm ${
                                            selectedCar?.key === car.key ? 'text-white' : 'text-zinc-900'
                                        }`}
                                    >
                                        {car.label}
                                    </h5>
                                    <div
                                        className={`flex items-center gap-2 text-[10px] ${
                                            selectedCar?.key === car.key ? 'text-white/80' : 'text-zinc-400'
                                        }`}
                                    >
                                        <span className="flex items-center gap-1">
                                            <HiMapPin size={10} /> {etaFor(car)}
                                        </span>
                                        <span className="uppercase">{car.body_type}</span>
                                        <span className="flex items-center gap-1 font-bold">● {car.seats}</span>
                                    </div>
                                </div>
                            </div>
                            <span
                                className={`font-bold text-sm ${
                                    selectedCar?.key === car.key ? 'text-white' : 'text-zinc-900'
                                }`}
                            >
                                {priceFor(car)}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="space-y-4">
                    <button
                        disabled={!selectedCar}
                        onClick={() => setSidebarStep('request')}
                        className={`w-full text-white py-4 rounded-xl font-bold dm-sans uppercase tracking-[1px] shadow-lg transition-all ${
                            selectedCar
                                ? 'bg-gradient-to-r from-[#1660C3] to-[#2671D8] shadow-blue-200/50'
                                : 'bg-zinc-300 shadow-none cursor-not-allowed'
                        }`}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );

    const renderRequestRide = () => {
        const est = selectedCar ? estimates[selectedCar.vehicle_class] : null;
        return (
            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={() => setSidebarStep('selection')}
                        className="p-2 hover:bg-zinc-100 rounded-full"
                    >
                        <HiArrowLeft className="text-xl" />
                    </button>
                    <h2 className="text-xl audiowide-regular uppercase text-[#0E0E0E]">Request Ride</h2>
                    <div className="w-8" />
                </div>

                <h3 className="text-lg audiowide-regular uppercase mb-4 text-zinc-900">Car Selected</h3>

                <div className="bg-[#D9E8FF] p-6 rounded-[2rem] relative mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <img
                            src={imageForCar(selectedCar || {})}
                            alt={selectedCar?.label}
                            className="w-32 h-20 object-contain"
                        />
                        <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-[#1660C3]" />
                            <span className="text-[10px] font-bold text-[#1660C3]">SUGGESTED</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-bold text-lg text-zinc-900">{selectedCar?.label}</h4>
                            <div className="flex items-center gap-4 text-xs text-zinc-600 mt-1">
                                <span className="flex items-center gap-1">
                                    <HiMapPin /> {etaFor(selectedCar || {})}
                                </span>
                                <span className="uppercase">{selectedCar?.body_type}</span>
                                <span className="flex items-center gap-1 font-bold">● {selectedCar?.seats}</span>
                            </div>
                        </div>
                        <span className="text-xl font-bold text-zinc-900">
                            {selectedCar ? priceFor(selectedCar) : '—'}
                        </span>
                    </div>
                    {est && (
                        <div className="flex items-center gap-4 text-[11px] text-zinc-600 mt-4 pt-4 border-t border-white/60">
                            <span>{est.distance_km?.toFixed(1)} km</span>
                            <span>~{Math.round(est.duration_min)} min</span>
                            {est.surge_multiplier > 1 && (
                                <span className="text-[#1660C3] font-bold">{est.surge_multiplier}× surge</span>
                            )}
                        </div>
                    )}
                </div>

                <ErrorBanner />

                <div className="space-y-6 mt-4">
                    <div>
                        <label className="text-sm font-bold text-zinc-600 mb-2 block">Payment Method</label>
                        <div className="relative">
                            <select
                                value={payment}
                                onChange={(e) => setPayment(e.target.value)}
                                className="w-full bg-zinc-100/80 border-none rounded-xl p-4 outline-none text-sm font-bold appearance-none cursor-pointer"
                            >
                                <option>Cash</option>
                                <option>Credit Card</option>
                                <option>Wallet</option>
                            </select>
                            <HiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-bold text-zinc-600 mb-2 block">
                            Discount Code (Optional)
                        </label>
                        <input
                            type="text"
                            value={promo}
                            onChange={(e) => setPromo(e.target.value.toUpperCase())}
                            placeholder="Enter your discount Code"
                            className="w-full bg-zinc-100/80 border-none rounded-xl p-4 outline-none text-sm font-medium"
                        />
                    </div>

                    <button
                        onClick={placeRide}
                        disabled={busy}
                        className="w-full bg-gradient-to-r from-[#1660C3] to-[#2671D8] text-white py-5 rounded-2xl font-bold audiowide-regular uppercase tracking-[2px] shadow-xl shadow-blue-200/50 hover:opacity-95 transition-all mt-8 disabled:opacity-60"
                    >
                        {busy ? 'Requesting…' : authed ? 'Request Ride' : 'Log in & Request'}
                    </button>
                </div>
            </div>
        );
    };

    const renderSearching = () => (
        <div className="flex flex-col items-center justify-center h-full py-20">
            <h2 className="text-2xl audiowide-regular uppercase text-[#0E0E0E] mb-12 text-center">
                Searching For Car
            </h2>

            <div className="relative w-48 h-48 mb-12">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 rounded-full border-[12px] border-zinc-100 border-t-[#1660C3]"
                />
                <div className="absolute inset-4 rounded-full bg-zinc-50 flex items-center justify-center overflow-hidden">
                    <motion.img
                        animate={{ x: [-100, 100] }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: 'mirror' }}
                        src={imageForCar(selectedCar || {})}
                        alt="searching"
                        className="w-32 h-auto object-contain grayscale opacity-50"
                    />
                </div>
            </div>

            {ride && (
                <p className="text-xs text-zinc-500 mb-6 font-medium">
                    Booking #{ride.id?.slice(0, 8)} · {prettyStatus(ride.status)}
                </p>
            )}

            <button
                onClick={doCancelRide}
                className="bg-zinc-100/80 text-[#1660C3] px-12 py-4 rounded-xl font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
            >
                Cancel Ride
            </button>
        </div>
    );

    const renderCarArriving = () => {
        const hasDriver = Boolean(ride?.driver_id);
        return (
            <div className="flex flex-col h-full bg-[#F4F4F4] -mx-6 md:-mx-8 -my-8 p-6 md:p-8 overflow-y-auto scrollbar-hide">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-zinc-900">
                        {hasDriver ? 'Car Arriving' : 'Finding Your Driver'}
                    </h2>
                    <span className="text-xs text-zinc-500 font-medium tracking-tight">
                        {ride ? prettyStatus(ride.status) : ''}
                    </span>
                </div>

                {/* Driver Info */}
                <div className="bg-white p-4 rounded-2xl flex items-center justify-between mb-4 shadow-sm">
                    <div className="flex items-center gap-4">
                        <img
                            src={driverProfile}
                            alt="Driver"
                            className="w-14 h-14 rounded-full object-cover border-2 border-zinc-100"
                        />
                        <div>
                            <h4 className="font-bold text-zinc-900">
                                {hasDriver ? 'Your Driver' : 'Matching you…'}
                            </h4>
                            <p className="text-xs text-zinc-400 font-medium">
                                {hasDriver ? `ID ${ride.driver_id.slice(0, 8)}` : 'Please wait'}
                            </p>
                        </div>
                    </div>
                    <div
                        onClick={() => hasDriver && setSidebarStep('chat')}
                        className={`p-3 rounded-xl shadow-lg shadow-blue-100 ${
                            hasDriver
                                ? 'bg-[#1660C3] text-white cursor-pointer hover:opacity-90'
                                : 'bg-zinc-200 text-zinc-400'
                        }`}
                    >
                        <FaCommentDots className="text-lg" />
                    </div>
                </div>

                {/* Route Card */}
                <div className="bg-white p-6 rounded-2xl mb-4 shadow-sm">
                    <h5 className="font-bold text-zinc-900 mb-4">Your Route</h5>
                    <div className="relative pl-8 space-y-8">
                        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 border-l-2 border-dashed border-zinc-200" />
                        <div className="relative">
                            <div className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-black border-4 border-white shadow-sm" />
                            <h6 className="text-[14px] font-bold text-zinc-900">Pickup</h6>
                            <p className="text-[12px] text-zinc-400 font-medium">
                                {ride?.pickup_address || pickup?.address}
                            </p>
                        </div>
                        <div className="relative">
                            <div className="absolute -left-[35px] top-1 flex items-center justify-center">
                                <HiMapPin className="text-2xl text-[#1660C3]" />
                            </div>
                            <h6 className="text-[14px] font-bold text-zinc-900">Destination</h6>
                            <p className="text-[12px] text-zinc-400 font-medium">
                                {ride?.dropoff_address || dropoff?.address}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Ride Details Card */}
                <div className="bg-white p-6 rounded-2xl mb-4 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h5 className="font-bold text-zinc-900">Ride Details</h5>
                        <span className="text-[10px] text-zinc-400 font-bold uppercase">
                            Booking ID: {ride?.id?.slice(0, 8)}
                        </span>
                    </div>

                    <h6 className="text-sm font-bold text-zinc-800 border-b border-zinc-50 pb-4 mb-4">
                        {selectedCar?.label} · {selectedCar?.body_type}
                    </h6>

                    <div className="space-y-4">
                        {[
                            {
                                label: 'Total Distance',
                                value: ride ? `${ride.distance_km?.toFixed(1)} km` : '—'
                            },
                            {
                                label: 'Total Fare',
                                value: ride ? money(ride.currency || currency, ride.total_fare) : '—'
                            },
                            { label: 'Surge', value: ride ? `${ride.surge_multiplier}×` : '—' },
                            { label: 'Payment Method', value: payment }
                        ].map((item, i) => (
                            <div
                                key={i}
                                className="flex justify-between items-center pb-2 border-b border-zinc-50 last:border-0 last:pb-0"
                            >
                                <span className="text-xs font-bold text-zinc-400">{item.label}</span>
                                <span className="text-xs font-bold text-zinc-800">{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <button className="flex items-center justify-center gap-4 bg-zinc-100 p-5 rounded-2xl font-bold mb-4 hover:bg-zinc-200 transition-colors">
                    <div className="bg-[#1660C3] p-2 rounded-lg text-white">
                        <FaShareAlt className="text-sm" />
                    </div>
                    <span className="text-sm text-zinc-800">Share Ride Details</span>
                </button>

                <button
                    onClick={doCancelRide}
                    className="w-full bg-[#D9E8FF] text-[#1660C3] py-5 rounded-3xl font-bold uppercase tracking-widest text-sm hover:bg-[#C9E1FF] transition-colors"
                >
                    Cancel Ride
                </button>
            </div>
        );
    };

    const renderChat = () => (
        <div className="flex flex-col h-full -mx-4">
            <div className="flex items-center gap-4 pb-6 border-b border-zinc-100 mb-6">
                <button
                    onClick={() => setSidebarStep('arriving')}
                    className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
                >
                    <FaChevronLeft className="text-lg text-blue-600" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-100">
                        <img src={driverProfile} alt="Driver" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-xl font-bold text-zinc-900">Your Driver</h3>
                </div>
            </div>

            <div className="flex-grow space-y-6 overflow-y-auto pr-2 scrollbar-hide py-2">
                <div className="flex justify-start">
                    <div className="bg-[#D1DCEE] text-zinc-900 p-4 px-6 rounded-tr-[25px] rounded-tl-[25px] rounded-br-[25px] max-w-[85%] shadow-sm">
                        <p className="text-sm font-medium">On my way — see you at the pickup point.</p>
                    </div>
                </div>
            </div>

            <div className="pt-6 mt-auto">
                <div className="relative flex items-center gap-2">
                    <div className="flex-grow relative">
                        <FaSmile className="absolute left-5 top-1/2 -translate-y-1/2 text-blue-600 text-xl cursor-pointer" />
                        <input
                            type="text"
                            placeholder="Type your message"
                            className="w-full pl-14 pr-14 py-4 bg-[#D1DCEE]/60 rounded-2xl border-none focus:ring-2 focus:ring-blue-400 transition-all outline-none text-blue-900 placeholder:text-blue-500 font-medium text-sm"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white cursor-pointer hover:bg-blue-700 transition-colors">
                            <HiPhone className="text-sm" />
                        </div>
                    </div>
                    <button className="w-10 h-10 flex items-center justify-center text-blue-600 hover:scale-110 transition-transform">
                        <FaPaperPlane className="text-xl" />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderForWhomModal = () => (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-[2rem] p-10 w-full max-w-md relative animate-in fade-in zoom-in duration-300">
                <button
                    onClick={() => setStep('booking')}
                    className="absolute top-8 right-8 text-zinc-400 hover:text-black transition-colors"
                >
                    <HiXMark className="text-2xl" />
                </button>

                <h2 className="text-md audiowide-regular uppercase text-[#0E0E0E] mb-10 pr-8">
                    Who you want to give this ride?
                </h2>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <p className="text-[10px] uppercase font-bold text-zinc-400">Phone</p>
                        <input
                            type="tel"
                            placeholder="+1 XXX XXXXXXX"
                            className="w-full bg-zinc-100 border border-zinc-100 rounded-2xl p-5 outline-none font-medium"
                        />
                    </div>
                    <div className="space-y-2">
                        <p className="text-[10px] uppercase font-bold text-zinc-400">Name</p>
                        <input
                            type="text"
                            placeholder="Passenger Name"
                            className="w-full bg-zinc-100 border border-zinc-100 rounded-2xl p-5 outline-none font-medium"
                        />
                    </div>
                    <button
                        onClick={() => setStep('booking')}
                        className="w-full bg-gradient-to-r from-[#1660C3] to-[#2671D8] text-white py-4 rounded-[1.5rem] font-bold dm-sans uppercase tracking-wider shadow-lg shadow-blue-200/50 hover:opacity-90 transition-opacity mt-2 flex items-center justify-center gap-2"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );

    const renderPhoneModal = () => (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-[2rem] p-6 lg:p-7 w-full max-w-[380px] relative animate-in fade-in zoom-in duration-300 md:overflow-visible overflow-y-auto scrollbar-hide max-h-[90vh] md:max-h-none">
                <button
                    onClick={() => setStep('booking')}
                    className="absolute top-5 right-6 text-blue-500 hover:scale-110 transition-transform"
                >
                    <HiXMark className="text-xl" />
                </button>

                <h2 className="text-lg audiowide-regular uppercase text-[#0E0E0E] mb-1">Log In / Sign Up</h2>
                <p className="text-xs text-zinc-400 mb-6 dm-sans">
                    We'll text you a one-time code to confirm your number.
                </p>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-zinc-800 ml-1 uppercase">Full Name</label>
                        <input
                            type="text"
                            value={pendingName}
                            onChange={(e) => setPendingName(e.target.value)}
                            placeholder="Enter your name"
                            className="w-full bg-zinc-100/80 border-none rounded-xl p-3 outline-none text-sm font-medium placeholder:text-zinc-400 focus:bg-zinc-100 transition-all"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-zinc-800 ml-1 uppercase">Phone Number</label>
                        <div className="flex bg-zinc-100/80 rounded-xl overflow-hidden">
                            <div className="flex items-center gap-1.5 px-3 border-r border-zinc-200">
                                <span className="text-lg">🇨🇦</span>
                            </div>
                            <input
                                type="tel"
                                value={pendingPhone}
                                onChange={(e) => setPendingPhone(e.target.value)}
                                placeholder="+1 204 555 0134"
                                className="w-full bg-transparent border-none p-3 outline-none text-sm font-medium placeholder:text-zinc-400"
                            />
                        </div>
                    </div>

                    <ErrorBanner />

                    <button
                        onClick={sendOtp}
                        disabled={busy}
                        className="w-full bg-[#1660C3] text-white py-3.5 rounded-xl font-bold uppercase tracking-wider shadow-lg shadow-blue-100 active:scale-95 transition-all mt-1 disabled:opacity-60"
                    >
                        {busy ? 'Sending…' : 'Send Code'}
                    </button>

                    <div className="text-center space-y-4 pt-1">
                        <p className="text-[9px] font-black audiowide-regular uppercase tracking-widest text-zinc-400">
                            Or continue using
                        </p>
                        <div className="flex justify-center gap-6">
                            <button className="hover:scale-110 transition-transform text-[#1660C3]">
                                <FaGoogle className="text-3xl sm:text-4xl" />
                            </button>
                            <button className="hover:scale-110 transition-transform text-black">
                                <FaApple className="text-3xl sm:text-4xl" />
                            </button>
                        </div>
                        <p className="text-[10px] text-zinc-400 leading-tight px-1 uppercase tracking-tighter">
                            By continuing, you accept the{' '}
                            <span className="text-[#1660C3] font-bold cursor-pointer">User Agreement</span> &{' '}
                            <span className="text-[#1660C3] font-bold cursor-pointer">Privacy Policy</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderOTPModal = () => (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-[2rem] p-10 w-full max-w-md relative animate-in fade-in zoom-in duration-300 text-center">
                <button
                    onClick={() => setStep('phone')}
                    className="absolute top-8 left-8 text-zinc-400 hover:text-black transition-colors"
                >
                    <HiArrowLeft className="text-2xl" />
                </button>

                <h2 className="text-md audiowide-regular uppercase text-[#0E0E0E] mb-4">
                    ENTER THE 6-DIGIT CODE
                </h2>
                <p className="text-zinc-500 dm-sans mb-2">
                    We sent it to <span className="text-[#0E0E0E] font-bold">{pendingPhone}</span> via SMS
                </p>
                {devHint && <p className="text-[11px] text-[#1660C3] font-bold mb-8">{devHint}</p>}

                <div className="space-y-8">
                    <div className="flex justify-center gap-3">
                        {otp.map((d, i) => (
                            <input
                                key={i}
                                ref={(el) => (otpRefs.current[i] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength="1"
                                value={d}
                                onChange={(e) => setOtpDigit(i, e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Backspace' && !otp[i] && i > 0)
                                        otpRefs.current[i - 1]?.focus();
                                }}
                                className="w-12 h-16 bg-zinc-100 border border-zinc-300 rounded-xl text-center text-2xl font-black outline-none focus:border-[#1660C3] transition-colors"
                            />
                        ))}
                    </div>

                    <ErrorBanner />

                    <div className="pt-2">
                        <p className="text-sm text-zinc-500 mb-2">Didn't receive it?</p>
                        <button
                            onClick={sendOtp}
                            className="text-[#1660C3] font-bold underline uppercase tracking-wider text-xs"
                        >
                            Resend It
                        </button>
                    </div>

                    <button
                        onClick={verifyAndBook}
                        disabled={busy}
                        className="w-full bg-gradient-to-r from-[#1660C3] to-[#2671D8] text-white py-4 rounded-[1.5rem] font-bold dm-sans uppercase tracking-wider shadow-lg shadow-blue-200/50 hover:opacity-90 transition-opacity mt-2 flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {busy ? 'Verifying…' : 'Verify & Complete'}
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="relative h-[calc(100vh-72px)] lg:h-[calc(100vh-84px)] w-full bg-zinc-100 overflow-hidden mt-[72px] lg:mt-[84px]">
            {/* Real Map (Google Maps Embed, centred on the active market / pickup) */}
            <div className="absolute inset-0 z-0">
                <iframe
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    scrolling="no"
                    marginHeight="0"
                    marginWidth="0"
                    title="Riden Map"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(mapCenter)}&z=13&output=embed`}
                    className="grayscale contrast-125 opacity-80"
                ></iframe>
                <div className="absolute inset-0 bg-[#1660C3]/5 pointer-events-none" />
            </div>

            {/* Sidebar */}
            <div
                className={`absolute top-0 left-0 h-full w-[320px] sm:w-[380px] lg:w-[450px] bg-white shadow-2xl px-6 md:px-8 pt-8 pb-0 z-40 overflow-y-auto scrollbar-hide flex flex-col transition-transform duration-500 ease-in-out ${
                    isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="absolute top-4 right-4 p-2 hover:bg-zinc-100 rounded-full transition-colors z-30"
                >
                    <HiXMark className="text-xl text-zinc-600" />
                </button>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={sidebarStep}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                        className="h-full"
                    >
                        {sidebarStep === 'details' && renderRideDetails()}
                        {sidebarStep === 'selection' && renderCarSelection()}
                        {sidebarStep === 'request' && renderRequestRide()}
                        {sidebarStep === 'searching' && renderSearching()}
                        {sidebarStep === 'arriving' && renderCarArriving()}
                        {sidebarStep === 'chat' && renderChat()}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Open Drawer Button */}
            {!isDrawerOpen && (
                <button
                    onClick={() => setIsDrawerOpen(true)}
                    className="absolute top-1/2 -translate-y-1/2 left-0 bg-white shadow-[10px_0_20px_rgba(0,0,0,0.1)] p-4 rounded-r-2xl z-10 hover:bg-zinc-50 transition-colors border border-l-0 border-zinc-200"
                >
                    <HiChevronRight className="text-2xl text-[#1660C3]" />
                </button>
            )}

            {step === 'for_whom' && renderForWhomModal()}
            {step === 'phone' && renderPhoneModal()}
            {step === 'otp' && renderOTPModal()}

            {/* Map Interactive HUD */}
            <div className="absolute top-10 right-10 flex flex-col gap-4 z-10">
                <button className="w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-xl text-zinc-600 hover:text-[#1660C3] transition-colors">
                    <HiMapPin />
                </button>
            </div>
        </div>
    );
};

export default BookingPage;
